'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';

import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import Link from 'next/link';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';

interface BlogsProps {
  blogs: Blog[];
  subtitle?: string;
  title?: string;
  description?: string;
}

const CAROUSEL_OPTIONS = { align: 'start' as const };

export function BlogsInner({ blogs, subtitle, title, description }: BlogsProps) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'relative gap-6 bg-[#FCFBFF] pb-10 pt-10 sm:gap-8 sm:pb-[50px] md:gap-10 lg:gap-[50px] lg:pt-12',
      )}
    >
      {/* <Image
        src={'/images/bottom-white.svg'}
        alt="Blog"
        width={0}
        height={0}
        className="absolute left-0 top-0 h-auto w-full object-contain"
      /> */}
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:items-end md:justify-between',
        )}
      >
        <Heading title={title ?? ''} subtitle={subtitle} />
        {!!description && (
          <Paragraph className="w-full max-w-[486px]" color="foreground" size="base">
            {description}
          </Paragraph>
        )}
      </div>
      <div
        className={cn('custom-container', 'overflow-visible')}
        style={{ clipPath: 'inset(-100vw -100vw -100vw 0)' }}
      >
        <Carousel
          options={CAROUSEL_OPTIONS}
          className={{
            wrapper: 'w-full overflow-visible',
            container: 'w-full gap-3 overflow-visible sm:gap-4 lg:gap-6',
            main: 'w-full overflow-visible',
            slide: 'w-full max-w-[320px] rounded-[12px] bg-white sm:max-w-[384px]',
          }}
          slides={blogs.map(blog => (
            <BlogCard blog={blog} key={blog.id} />
          ))}
        />
      </div>
      <div className={cn('custom-container', 'flex items-center justify-between pr-4 xl:pr-0')}>
        <CarouselControls
          buttonProps={{ size: 'none' }}
          nextIcon={<GoArrowRight size={20} />}
          prevIcon={<GoArrowLeft size={20} />}
          className={{
            main: 'hidden sm:flex',
            button:
              'max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-[12px] bg-[#E4F2E7] p-0 text-primary hover:bg-[#E4F2E7]',
          }}
        />
        <div className="flex w-full flex-col items-center justify-center gap-3 sm:w-fit sm:flex-row sm:justify-end">
          <Button asChild variant={'primary-foreground'} className="w-full sm:w-fit">
            <Link href={ROUTES.BLOG()} prefetch={false}>
              {t('text3876')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export function Blogs(props: BlogsProps) {
  return (
    <CarouselProvider>
      <BlogsInner {...props} />
    </CarouselProvider>
  );
}
